import type { ContentTypeName } from "./core"

export type ContentFieldKind =
  | "string"
  | "text"
  | "markdown"
  | "date"
  | "locale"
  | "year"
  | "careerTeam"
  | "boolean"
  | "url"
  | "image"
  | "imageArray"
  | "stringArray"

export type ContentFieldDefinition = {
  name: string
  label: string
  kind: ContentFieldKind
  required?: boolean
  readOnly?: boolean
  group?: string
  description?: string
}

export type ContentTypeDefinition = {
  type: ContentTypeName
  label: string
  description: string
  fields: ContentFieldDefinition[]
}

const commonSeoFields: ContentFieldDefinition[] = [
  { name: "metaDescription", label: "Meta Description", kind: "text", group: "SEO" },
  { name: "metaOgTitle", label: "Meta og:title", kind: "string", group: "SEO" },
  { name: "metaOgDescription", label: "Meta og:description", kind: "text", group: "SEO" },
]

export const contentTypeDefinitions: ContentTypeDefinition[] = [
  {
    type: "news",
    label: "News",
    description: "News articles and detail-page metadata.",
    fields: [
      { name: "language", label: "Language", kind: "locale", required: true, group: "Publishing" },
      { name: "publishDate", label: "Publish date", kind: "date", group: "Publishing" },
      { name: "title", label: "Title", kind: "string", required: true, group: "Identity" },
      { name: "slug", label: "Slug", kind: "string", required: true, group: "Identity" },
      { name: "newsCoverBlurb", label: "News Cover Blurb", kind: "text", required: true, group: "Content" },
      { name: "bio", label: "Content Markdown", kind: "markdown", required: true, group: "Content" },
      { name: "newsCoverImage", label: "News Cover Image", kind: "image", required: true, group: "Media" },
      ...commonSeoFields,
    ],
  },
  {
    type: "careers",
    label: "Careers",
    description: "Job listings and career detail pages.",
    fields: [
      { name: "language", label: "Language", kind: "locale", required: true, group: "Publishing" },
      { name: "active", label: "Active", kind: "boolean", required: true, group: "Publishing" },
      { name: "publishDate", label: "Publish date", kind: "date", group: "Publishing" },
      { name: "title", label: "Position Name", kind: "string", required: true, group: "Identity" },
      { name: "teamKey", label: "Team", kind: "careerTeam", required: true, group: "Identity" },
      { name: "slug", label: "Slug", kind: "string", required: true, group: "Identity" },
      { name: "newsBlurb", label: "Career Blurb", kind: "text", required: true, group: "Content" },
      { name: "description", label: "Content Markdown", kind: "markdown", required: true, group: "Content" },
      { name: "newsCoverImage", label: "Career Cover Image", kind: "image", required: true, group: "Media" },
      ...commonSeoFields,
    ],
  },
  {
    type: "workProducts",
    label: "Work Products",
    description: "Product entries shown under the work page products section.",
    fields: [
      { name: "language", label: "Language", kind: "locale", required: true, group: "Publishing" },
      { name: "title", label: "Title", kind: "string", required: true, group: "Identity" },
      { name: "url", label: "URL", kind: "url", required: true, group: "Identity" },
      { name: "coverImage", label: "Cover Image", kind: "image", required: true, group: "Media" },
    ],
  },
  {
    type: "workMarketing",
    label: "Work Marketing",
    description: "Marketing and production work records shown in gallery sections.",
    fields: [
      { name: "language", label: "Language", kind: "locale", required: true, group: "Publishing" },
      { name: "title", label: "Title", kind: "string", required: true, group: "Identity" },
      { name: "coverImage", label: "Cover Image", kind: "image", required: true, group: "Media" },
      { name: "gallery", label: "Gallery", kind: "imageArray", required: true, group: "Media" },
    ],
  },
  {
    type: "workTags",
    label: "Work Tags",
    description: "Localized tag lists shown across the work page sections.",
    fields: [
      { name: "language", label: "Language", kind: "locale", required: true, group: "Publishing" },
      { name: "title", label: "Title", kind: "string", required: true, group: "Identity" },
      {
        name: "esportsBroadcastTags",
        label: "Esports & Broadcast Tags",
        kind: "stringArray",
        group: "Tags",
      },
      {
        name: "gameCultureMarketingTags",
        label: "Game Culture Marketing Tags",
        kind: "stringArray",
        group: "Tags",
      },
      { name: "productTags", label: "Product Tags", kind: "stringArray", group: "Tags" },
    ],
  },
  {
    type: "companyHistory",
    label: "Company History",
    description: "Timeline entries and supporting images for the company page.",
    fields: [
      { name: "language", label: "Language", kind: "locale", required: true, group: "Publishing" },
      { name: "title", label: "Title", kind: "string", required: true, group: "Identity" },
      { name: "year", label: "Year", kind: "year", required: true, group: "Identity" },
      { name: "description", label: "Description", kind: "text", required: true, group: "Content" },
      { name: "images", label: "Images", kind: "imageArray", group: "Media" },
    ],
  },
]

export function getContentTypeDefinition(type: ContentTypeName) {
  return contentTypeDefinitions.find((definition) => definition.type === type) ?? contentTypeDefinitions[0]
}
